import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, useTheme } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { copyToClipboard } from '../utils/clipboardUtils';

/**
 * CodeSnippetRenderer component displays a JSON payload with syntax highlighting
 * @param {string} title - Title for the code snippet section
 * @param {string|Object} payload - JSON payload (string or object) to display
 */
const CodeSnippetRenderer = ({ title, payload }) => {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  if (!payload) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
          No payload available
        </Typography>
      </Box>
    );
  }

  // Format JSON for display - handle both string and object payloads
  let codeString = '';
  if (typeof payload === 'string') {
    // If it's a string, try to parse and format it
    try {
      codeString = JSON.stringify(JSON.parse(payload), null, 2);
    } catch (error) {
      // If it's not valid JSON, use as-is
      codeString = payload;
    }
  } else {
    // If it's an object, stringify it directly
    codeString = JSON.stringify(payload, null, 2);
  }

  const handleCopy = async () => {
    const success = await copyToClipboard(codeString);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header Box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: theme.palette.background.paper,
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy to Clipboard'} arrow>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'text.primary',
                },
              }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {/* SyntaxHighlighter Box */}
        <Box
          sx={{
            maxHeight: 'none',
          }}
        >
          <SyntaxHighlighter
            language="json5"
            style={oneDark}
            wrapLines={true}
            wrapLongLines={true}
            lineProps={() => ({
              style: { backgroundColor: 'transparent' },
            })}
            customStyle={{
              margin: 0,
              padding: '16px',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              lineHeight: 1.6,
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
            PreTag="div"
            showLineNumbers={false}
          >
            {codeString}
          </SyntaxHighlighter>
        </Box>
      </Box>
    </Box>
  );
};

export default CodeSnippetRenderer;
